import { Link } from "react-router-dom";
import { Calendar, MapPin, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EventData } from "@/types/events";
import { useLanguage } from "@/contexts/LanguageContext";

interface EventCardProps {
  event: EventData;
}

const EventCard = ({ event }: EventCardProps) => {
  const { language, t } = useLanguage();
  const isRTL = language === 'ar';
  const eventTitle = event.title?.trim() || t("events.noTitle");
  const eventDescription = (typeof event.description === "string" ? event.description : "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim() || t("events.noDescription");
  const typeEN = event.type?.en;

  const typeStyles =
    typeEN === "Webinar"
      ? {
          bg: "bg-nexus-teal/10",
          icon: "text-nexus-teal",
          badge: "bg-nexus-teal/20 text-nexus-teal",
        }
      : typeEN === "Workshop"
      ? {
          bg: "bg-nexus-yellow/10",
          icon: "text-nexus-yellow",
          badge: "bg-nexus-yellow/20 text-nexus-gray",
        }
      : typeEN === "Talk"
      ? {
          bg: "bg-nexus-red/10",
          icon: "text-nexus-red",
          badge: "bg-nexus-red/20 text-nexus-red",
        }
      : {
          bg: "bg-gray-100",
          icon: "text-gray-400",
          badge: "bg-gray-200 text-gray-600",
        };

  const getBadgeStyle = () => {
    switch (event.badge) {
      case "Popular":
        return "bg-nexus-yellow text-nexus-gray";
      case "New":
        return "bg-nexus-red text-white";
      case "Featured":
        return "bg-purple-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  return (
    <Card className={`border border-gray-200 transition-all hover:shadow-md ${isRTL ? 'rtl' : 'ltr'}`} id="event-card" dir={isRTL ? 'rtl' : 'ltr'}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-1/4">
            <div className={`w-full aspect-video rounded-lg overflow-hidden ${typeStyles.bg}`}>
              {event.imageURL && event.imageURL.trim() !== "" ? (
                <img
                  src={event.imageURL}
                  alt={event.title || "Event image"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Calendar className={`h-10 w-10 ${typeStyles.icon}`} />
                </div>
              )}
            </div>
          </div>

          <div className="md:w-3/4">
            <div className="flex flex-wrap gap-2 mb-2">
              {event.type?.[language] && (
                <Badge className={typeStyles.badge}>
                  {event.type[language]}
                </Badge>
              )}

              {event.badge && (
                <Badge className={getBadgeStyle()}>{event.badge}</Badge>
              )}
            </div>

            <h2 className="text-xl font-semibold text-nexus-gray dark:text-white mb-2">
              <Link to={`/events/${event.id}`} className="hover:text-nexus-teal">
                {eventTitle}
              </Link>
            </h2>

            <p className="text-gray-600 dark:text-white mb-3 line-clamp-4 overflow-hidden">{eventDescription}</p>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-300">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>
                  {event.date}, {event.time}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{event.speaker?.name}</span>
              </div>
              {event.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{event.location}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100">
              <div className="text-sm text-gray-500 dark:text-gray-300">
                {event.attendees} {t("events.attending")}
              </div>

              {event.GoogleFormURL ? (
                <Button
                  asChild
                  size="sm"
                  className="bg-nexus-brand hover:bg-nexus-brand/90"
                >
                  <a href={event.GoogleFormURL} target="_blank" rel="noopener noreferrer">
                    {t("events.register")}
                  </a>
                </Button>
              ) : (
                <Button disabled size="sm" className="bg-gray-300 text-gray-600">
                  {t("events.registerUnavailable")}
                </Button>
              )}

              <Button
                asChild
                variant="outline"
                size="sm"
                className="text-nexus-gray dark:text-nexus-light border-gray-300"
              >
                <Link to={`/events/${event.id}`}>{t("events.viewDetails")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EventCard;
